import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Clock, Sparkles, ArrowRight } from 'lucide-react';

interface ComingSoonProps {
  title?: string;
  description?: string;
  expectedDate?: string;
  features?: string[];
  onNotifyMe?: () => void;
  variant?: 'default' | 'minimal' | 'detailed';
  className?: string;
}

export function ComingSoon({
  title = "Coming Soon",
  description = "We're working on something amazing. Stay tuned for updates!",
  expectedDate,
  features = [],
  onNotifyMe,
  variant = 'default',
  className = "",
}: ComingSoonProps) {
  const renderMinimal = () => (
    <div className={`flex flex-col items-center justify-center p-8 text-center ${className}`}>
      <div className="mb-4 rounded-full bg-muted p-3">
        <Clock className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2">{description}</p>
      {expectedDate && (
        <Badge variant="secondary" className="mt-3">
          Expected: {expectedDate}
        </Badge>
      )}
    </div>
  );

  const renderDetailed = () => (
    <Card className={`w-full max-w-2xl mx-auto ${className}`}>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 p-4">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription className="text-base">{description}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {expectedDate && (
          <div className="text-center">
            <Badge variant="outline" className="text-sm">
              <Clock className="mr-2 h-4 w-4" />
              Expected: {expectedDate}
            </Badge>
          </div>
        )}

        {features.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="font-medium mb-3">What to expect:</h4>
              <ul className="space-y-2">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-muted-foreground">
                    <div className="mr-3 h-1.5 w-1.5 rounded-full bg-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {onNotifyMe && (
          <>
            <Separator />
            <div className="text-center">
              <Button onClick={onNotifyMe} className="w-full sm:w-auto">
                Notify me when ready
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderDefault = () => (
    <Card className={`w-full max-w-lg mx-auto ${className}`}>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 rounded-full bg-muted p-3">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {expectedDate && (
          <div className="text-center">
            <Badge variant="secondary">
              Expected: {expectedDate}
            </Badge>
          </div>
        )}

        {onNotifyMe && (
          <div className="text-center">
            <Button onClick={onNotifyMe} variant="outline" className="w-full">
              Notify me when ready
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  switch (variant) {
    case 'minimal':
      return renderMinimal();
    case 'detailed':
      return renderDetailed();
    default:
      return renderDefault();
  }
}

